import type { TokenSetting } from '../../shared/control-elements/controls/types';
import type { ComponentSettingCategory, ComponentSettingDefinition, ComponentSettingVisibilityCondition, ComponentTokenData, ComponentTokenReferenceSetting } from '../../shared/types/designBuilderDataTypes';

function isRecord(value: unknown): value is Record<string, unknown> {
	return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeStringArray(input: unknown): string[] {
	if (!Array.isArray(input)) {
		return [];
	}

	return input
		.filter((value): value is string => typeof value === 'string')
		.map((value) => value.trim())
		.filter(Boolean);
}

function normalizeVisibleWhen(input: unknown): ComponentSettingVisibilityCondition | undefined {
	if (!isRecord(input)) {
		return undefined;
	}

	const visibleWhen: ComponentSettingVisibilityCondition = {};
	const hasClass = normalizeStringArray(input.hasClass);
	const hasAnyClass = normalizeStringArray(input.hasAnyClass);
	const doesNotHaveClass = normalizeStringArray(input.doesNotHaveClass);
	const settingEquals = Array.isArray(input.settingEquals)
		? input.settingEquals
				.filter(isRecord)
				.map((entry) => {
					const variable = typeof entry.variable === 'string' ? entry.variable.trim() : '';
					const value = entry.value !== undefined && entry.value !== null ? String(entry.value).trim() : '';
					return variable.startsWith('--') && value !== '' ? { variable, value } : null;
				})
				.filter((entry): entry is { variable: string; value: string } => entry !== null)
		: [];
	const settingNotEquals = Array.isArray(input.settingNotEquals)
		? input.settingNotEquals
				.filter(isRecord)
				.map((entry) => {
					const variable = typeof entry.variable === 'string' ? entry.variable.trim() : '';
					const value = entry.value !== undefined && entry.value !== null ? String(entry.value).trim() : '';
					return variable.startsWith('--') && value !== '' ? { variable, value } : null;
				})
				.filter((entry): entry is { variable: string; value: string } => entry !== null)
		: [];

	if (hasClass.length > 0) {
		visibleWhen.hasClass = hasClass;
	}

	if (hasAnyClass.length > 0) {
		visibleWhen.hasAnyClass = hasAnyClass;
	}

	if (doesNotHaveClass.length > 0) {
		visibleWhen.doesNotHaveClass = doesNotHaveClass;
	}

	if (settingEquals.length > 0) {
		visibleWhen.settingEquals = settingEquals;
	}

	if (settingNotEquals.length > 0) {
		visibleWhen.settingNotEquals = settingNotEquals;
	}

	return Object.keys(visibleWhen).length > 0 ? visibleWhen : undefined;
}

function normalizeComponentVariableSetting(setting: unknown): TokenSetting | null {
	if (!isRecord(setting)) {
		return null;
	}

	const variable = typeof setting.variable === 'string' ? setting.variable.trim() : '';
	const label = typeof setting.label === 'string' ? setting.label.trim() : '';
	const type = typeof setting.type === 'string' ? setting.type.trim() : '';
	const defaultValue = setting.default;

	if (!variable.startsWith('--') || label === '' || type === '' || defaultValue === undefined || defaultValue === null) {
		return null;
	}

	const normalized: TokenSetting = {
		variable,
		label,
		type: type as TokenSetting['type'],
		default: String(defaultValue),
	};

	if (typeof setting.description === 'string' && setting.description.trim() !== '') {
		normalized.description = setting.description.trim();
	}

	const visibleWhen = normalizeVisibleWhen(setting.visibleWhen);
	if (visibleWhen) {
		normalized.visibleWhen = visibleWhen;
	}

	if (typeof setting.unit === 'string') {
		normalized.unit = setting.unit;
	}

	if (typeof setting.locked === 'boolean') {
		normalized.locked = setting.locked;
	}

	if (typeof setting.min === 'number') {
		normalized.min = setting.min;
	}

	if (typeof setting.max === 'number') {
		normalized.max = setting.max;
	}

	if (typeof setting.step === 'number') {
		normalized.step = setting.step;
	}

	if (typeof setting.pairWith === 'string' && setting.pairWith.trim().startsWith('--')) {
		normalized.pairWith = setting.pairWith.trim();
	}

	if (isRecord(setting.rangeConstraint)) {
		const group = typeof setting.rangeConstraint.group === 'string' ? setting.rangeConstraint.group.trim() : '';
		const role = setting.rangeConstraint.role === 'min' || setting.rangeConstraint.role === 'max' ? setting.rangeConstraint.role : null;
		if (group !== '' && role) {
			normalized.rangeConstraint = {
				group,
				role,
			};
		}
	}

	if (Array.isArray(setting.outputs)) {
		const outputs = setting.outputs
			.filter((value): value is string => typeof value === 'string')
			.map((value) => value.trim())
			.filter((value) => value.startsWith('--'));

		if (outputs.length > 0) {
			normalized.outputs = [...new Set(outputs)];
		}
	}

	if (Array.isArray(setting.options)) {
		const options = setting.options
			.filter(isRecord)
			.map((option) => {
				const value = typeof option.value === 'string' ? option.value : null;
				const optionLabel = typeof option.label === 'string' ? option.label : null;
				return value !== null && optionLabel ? { value, label: optionLabel } : null;
			})
			.filter((option): option is { value: string; label: string } => option !== null);

		if (options.length > 0) {
			normalized.options = options;
		}
	}

	if (typeof setting.contrast === 'string') {
		normalized.contrast = setting.contrast;
	} else if (Array.isArray(setting.contrast)) {
		const contrast = setting.contrast.filter((value): value is string => typeof value === 'string' && value.trim() !== '');
		if (contrast.length > 0) {
			normalized.contrast = contrast;
		}
	}

	return normalized;
}

function normalizeComponentTokenReferenceSetting(setting: unknown): ComponentTokenReferenceSetting | null {
	if (!isRecord(setting)) {
		return null;
	}

	const token = typeof setting.token === 'string' ? setting.token.trim() : '';
	const label = typeof setting.label === 'string' ? setting.label.trim() : '';

	if (token === '' || label === '') {
		return null;
	}

	const normalized: ComponentTokenReferenceSetting = {
		token,
		label,
	};

	if (typeof setting.description === 'string' && setting.description.trim() !== '') {
		normalized.description = setting.description.trim();
	}

	if (typeof setting.includeStateColors === 'boolean') {
		normalized.includeStateColors = setting.includeStateColors;
	}

	const visibleWhen = normalizeVisibleWhen(setting.visibleWhen);
	if (visibleWhen) {
		normalized.visibleWhen = visibleWhen;
	}

	return normalized;
}

function normalizeComponentSetting(setting: unknown): ComponentSettingDefinition | null {
	return normalizeComponentVariableSetting(setting) ?? normalizeComponentTokenReferenceSetting(setting);
}

function normalizeComponentSettings(input: unknown): ComponentSettingCategory[] {
	if (!Array.isArray(input)) {
		return [];
	}

	return input
		.filter(isRecord)
		.map((category) => {
			const id = typeof category.id === 'string' ? category.id.trim() : '';
			const label = typeof category.label === 'string' ? category.label.trim() : '';
			const settings = Array.isArray(category.settings) ? category.settings.map(normalizeComponentSetting).filter((setting): setting is ComponentSettingDefinition => setting !== null) : [];

			if (id === '' || label === '' || settings.length === 0) {
				return null;
			}

			const normalized: ComponentSettingCategory = {
				id,
				label,
				settings,
			};

			if (typeof category.description === 'string' && category.description.trim() !== '') {
				normalized.description = category.description.trim();
			}

			if (category.present === 'swatch') {
				normalized.present = 'swatch';
			}

			return normalized;
		})
		.filter((category): category is ComponentSettingCategory => category !== null);
}

export function normalizeComponentName(value: string): string {
	return value.trim().toLowerCase().replace(/^c-/, '');
}

export function parseComponentTokenData(raw: unknown): ComponentTokenData {
	if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
		return {};
	}

	const parsed: ComponentTokenData = {};
	for (const [key, value] of Object.entries(raw)) {
		const normalizedKey = normalizeComponentName(key);
		if (!normalizedKey) continue;

		if (!value || typeof value !== 'object' || Array.isArray(value)) {
			continue;
		}

		const definition = value as Record<string, unknown>;
		parsed[normalizedKey] = {
			name: typeof definition.name === 'string' ? definition.name : undefined,
			slug: typeof definition.slug === 'string' ? normalizeComponentName(definition.slug) : normalizedKey,
			tokens: Array.isArray(definition.tokens)
				? definition.tokens
						.filter((token): token is string => typeof token === 'string')
						.map((token) => token.trim())
						.filter(Boolean)
				: [],
			componentSettings: normalizeComponentSettings(definition.componentSettings),
		};
	}

	return parsed;
}
